import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addRepairRequestLine,
  completeRepairRequestAfterRemoval,
  correctRepairRequest,
  correctRepairRequestLineSerial,
  fetchRepairRequestCompletionAfterRemoval,
  removeRepairRequestLine,
} from '#/api/aftersalesMgmt/repairRequest/corrections';
import { serializeRepairRequestCorrection } from '#/api/aftersalesMgmt/repairRequest/correctionSerialize';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import RepairRequestCorrectionDialog from
  '#/modules/crmMgmt/aftersales/repairRequest/components/RepairRequestCorrectionDialog';
import RepairRequestCorrectionHistoryDialog from
  '#/modules/crmMgmt/aftersales/repairRequest/components/RepairRequestCorrectionHistoryDialog';
import RepairRequestLineCommandDialog from
  '#/modules/crmMgmt/aftersales/repairRequest/components/RepairRequestLineCommandDialog';
import {
  getRepairRequestApiErrorMessage,
  isRepairRequestVersionConflict,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/apiError';
import {
  repairRequestCompletionAfterRemovalQueryKey,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/correctionQueryKeys';
import { canCorrectRepairRequest } from '#/modules/crmMgmt/aftersales/repairRequest/helper/repairRequestStatus';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { toast } from '#/utils/toast';
import {
  repairRequestDetailQueryKey,
  repairRequestListQueryKeyPrefix,
} from '#/utils/queryKeys';

/**
 * 成立後更正（be doc f77b08e9 #85–#88）：表頭／品項更正、更正 SN、新增／移除品項、
 * 移除後客服確認完成、更正紀錄。回傳 dialogs 與給 CommandBar／明細列用的 handler。
 *
 * @param {object} detail 報修單 detail（camel）
 * @param {() => Promise<unknown>|void} [onRefetch]
 */
export default function useRepairRequestCorrectionActions(detail, onRefetch) {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [lineDialog, setLineDialog] = useState({ mode: null, line: null });
  const [removeLine, setRemoveLine] = useState(null);

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? null;
  const enabled = canCorrectRepairRequest(detail?.status);

  const completionQuery = useQuery({
    queryKey: repairRequestCompletionAfterRemovalQueryKey(id),
    queryFn: () => fetchRepairRequestCompletionAfterRemoval(id),
    enabled: enabled && Boolean(id),
    retry: false,
  });
  const completionPending = Boolean(completionQuery.data?.completionConfirmationRequired);

  async function invalidateAll() {
    await queryClient.invalidateQueries({ queryKey: repairRequestListQueryKeyPrefix() });
    if (id) {
      await queryClient.invalidateQueries({ queryKey: repairRequestDetailQueryKey(id) });
    }
    await queryClient.invalidateQueries({ queryKey: ['erp', 'repairReport', 'forRepairLine'] });
    await onRefetch?.();
  }

  function handleError(error) {
    toast.error(getRepairRequestApiErrorMessage(error));
    if (isRepairRequestVersionConflict(error)) {
      onRefetch?.();
    }
  }

  const completeMutation = useMutation({
    mutationFn: (latestUpdatedAt) => completeRepairRequestAfterRemoval(id, {
      updatedAt: latestUpdatedAt,
    }),
    retry: false,
    onSuccess: async (result) => {
      toast.success(result?.message || '已完成報修單');
      await invalidateAll();
    },
    onError: handleError,
  });

  async function askCompletion(latestUpdatedAt) {
    const ok = await confirm({
      title: '完成報修單',
      message: '剩餘有效品項都已有最終報告。要現在完成這張報修單嗎？選「暫不完成」會維持處理中，之後仍可完成。',
      confirmText: '完成報修單',
      cancelText: '暫不完成',
    });
    if (ok) {
      completeMutation.mutate(latestUpdatedAt);
    }
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, lineId, payload }) => {
      if (action === 'correct') {
        return correctRepairRequest(id, payload);
      }
      if (action === 'serial') {
        return correctRepairRequestLineSerial(id, lineId, { ...payload, updatedAt });
      }
      if (action === 'add') {
        return addRepairRequestLine(id, { ...payload, updatedAt });
      }
      if (action === 'remove') {
        return removeRepairRequestLine(id, lineId, { ...payload, updatedAt });
      }
      throw new Error(`Unknown action: ${action}`);
    },
    retry: false,
    onSuccess: async (result) => {
      toast.success(result?.message || '更正成功');
      setCorrectionOpen(false);
      setLineDialog({ mode: null, line: null });
      setRemoveLine(null);
      await invalidateAll();
      if (result?.completionConfirmationRequired) {
        await askCompletion(result?.data?.record?.updatedAt ?? null);
      }
    },
    onError: handleError,
  });

  const isPending = commandMutation.isPending || completeMutation.isPending;

  function handleCorrectionSubmit(values) {
    if (!String(values?.reason ?? '').trim()) {
      toast.error('請填寫更正原因');
      return;
    }
    const body = serializeRepairRequestCorrection(detail, values);
    if (!body) {
      toast.info('沒有需要更正的資料');
      return;
    }
    commandMutation.mutate({ action: 'correct', payload: body });
  }

  const dialogs = id ? (
    <>
      {ConfirmDialogHost}
      <RepairRequestCorrectionDialog
        open={correctionOpen}
        onOpenChange={setCorrectionOpen}
        detail={detail}
        isPending={isPending}
        onSubmit={handleCorrectionSubmit}
      />
      <RepairRequestCorrectionHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        detail={detail}
      />
      <RepairRequestLineCommandDialog
        open={Boolean(lineDialog.mode)}
        onOpenChange={(open) => {
          if (!open) {
            setLineDialog({ mode: null, line: null });
          }
        }}
        mode={lineDialog.mode ?? 'serial'}
        currentSerialNo={lineDialog.line?.serialNo ?? ''}
        isPending={isPending}
        onSubmit={(payload) => commandMutation.mutate({
          action: lineDialog.mode,
          lineId: lineDialog.line?.id,
          payload,
        })}
      />
      <DocumentRejectReasonDialog
        open={Boolean(removeLine)}
        onOpenChange={(open) => {
          if (!open) {
            setRemoveLine(null);
          }
        }}
        title="移除報修品項"
        description={`移除 ${removeLine?.serialNo ?? ''}：不是拒修，客戶查詢將不再顯示此品項；內部保留紀錄。有維修報告草稿需先取消。`}
        confirmText="移除"
        reasonLabel="移除原因"
        emptyErrorMessage="請填寫移除原因"
        isPending={isPending}
        onConfirm={(reason) => commandMutation.mutate({
          action: 'remove',
          lineId: removeLine?.id,
          payload: { reason },
        })}
      />
    </>
  ) : null;

  return {
    enabled,
    isPending,
    completionPending,
    dialogs,
    openCorrection: () => setCorrectionOpen(true),
    openHistory: () => setHistoryOpen(true),
    openAddLine: () => setLineDialog({ mode: 'add', line: null }),
    openCorrectSerial: (line) => setLineDialog({ mode: 'serial', line }),
    openRemoveLine: (line) => setRemoveLine(line),
    completeAfterRemoval: () => askCompletion(updatedAt),
  };
}
